import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { buildPortfolioFromEntity } from '@/admins/SmartReports/helpers';
import { usePagination } from '@/admins/SmartReports/hooks/usePagination';
import type { SmartReportEntityExtended } from '@/admins/SmartReports/types';
import type {
  Portfolio,
  SmartReport,
  SmartReportPage,
  SmartReportVersion,
} from '@/types/GraphqlSchema';
import { consistentHash } from '@/utils/consistentHash';
import type { OnComponentLoad } from '../Components/types';
import type { LayoutDefinition } from '../Layouts/types';
import { ACTION_TYPES, editSmartReportReducer, INITIAL_STATE } from './EditSmartReportReducer';

export type SmartReportPageWithKey = SmartReportPage & { key: string };

export type EditSmartReportContextType = {
  report: SmartReport | null;
  reportVersion: SmartReportVersion | null;
  selectedPage: SmartReportPage | null;
  selectedLayout: LayoutDefinition | null;
  entity: SmartReportEntityExtended | null;
  portfolio: Portfolio | null;
  pages: SmartReportPageWithKey[];
  isDrawerOpen: boolean;
  setReport: (report: SmartReport | null) => void;
  setSelectedPage: (page: SmartReportPage | null) => void;
  setSelectedLayout: (layout: LayoutDefinition | null) => void;
  addPage: () => void;
  removePage: (page: SmartReportPageWithKey) => void;
  nextPage: () => void;
  previousPage: () => void;
  onComponentLoad: OnComponentLoad<unknown>;
  resetPageSlot: (slotId: string) => void;
  applyLayout: (layout: LayoutDefinition | null) => void;
  toggleDrawer: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
};

const parseEntityId = (entityId: unknown): number | string | null => {
  if (typeof entityId === 'number') return entityId;
  if (typeof entityId === 'string') {
    const parsed = Number.parseInt(entityId, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const EditSmartReportContext = createContext<EditSmartReportContextType | null>(null);

type EditSmartReportProviderProps = {
  children: ReactNode;
};

const EditSmartReportProvider = ({ children }: EditSmartReportProviderProps) => {
  const [state, dispatch] = useReducer(editSmartReportReducer, INITIAL_STATE);

  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  const entity = useMemo((): SmartReportEntityExtended | null => {
    const entityParent = state.report?.smartReportEntities?.[0];
    if (!entityParent?.entity) return null;

    return {
      ...entityParent,
      id: parseEntityId(entityParent.entityId),
      name: entityParent.entity.name,
      type: entityParent.entityType,
    } as SmartReportEntityExtended;
  }, [state.report]);

  const portfolio = useMemo(() => buildPortfolioFromEntity(entity), [entity]);

  const pages = useMemo((): EditSmartReportContextType['pages'] => {
    return (state.reportVersion?.pages || []).map((page: SmartReportPage) => {
      const { key, ...restPage } = page as SmartReportPage & { key?: string };
      return {
        ...restPage,
        key: key || consistentHash(page),
      };
    });
  }, [state.reportVersion]);

  const setReport = useCallback((report: EditSmartReportContextType['report']) => {
    dispatch({
      type: ACTION_TYPES.SET_REPORT,
      payload: { report },
    });
  }, []);

  const setSelectedPage = useCallback((page: EditSmartReportContextType['selectedPage']) => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_PAGE,
      payload: { page },
    });
  }, []);

  const setSelectedLayout = useCallback((layout: EditSmartReportContextType['selectedLayout']) => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_LAYOUT,
      payload: { layout },
    });
  }, []);

  const { nextPage, previousPage } = usePagination({
    selectedPage: state.selectedPage,
    pages,
    setSelectedPage,
  });

  const addPage = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.ADD_PAGE,
      payload: { layout: state.selectedLayout },
    });
  }, [state.selectedLayout]);

  const removePage = useCallback((page: EditSmartReportContextType['pages'][number]) => {
    if (!page?.number) return;

    dispatch({
      type: ACTION_TYPES.REMOVE_PAGE,
      payload: { pageNumber: page.number },
    });
  }, []);

  const resetPageSlot = useCallback((slotId: string) => {
    if (!slotId) return;

    dispatch({
      type: ACTION_TYPES.REMOVE_PAGE_ELEMENT,
      payload: { slotId },
    });
  }, []);

  const applyLayout = useCallback((layout: EditSmartReportContextType['selectedLayout']) => {
    if (!layout?.id) return;

    dispatch({
      type: ACTION_TYPES.APPLY_LAYOUT,
      payload: { layout },
    });
  }, []);

  const onComponentLoad = useCallback<EditSmartReportContextType['onComponentLoad']>(
    ({ componentId, slotId, data }) => {
      if (!componentId || !slotId) return;

      dispatch({
        type: ACTION_TYPES.UPDATE_PAGE_ELEMENT,
        payload: { componentId, slotId, data },
      });
    },
    [],
  );

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen(prev => !prev);
  }, []);

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const contextValue = useMemo<EditSmartReportContextType>(
    () => ({
      report: state.report,
      reportVersion: state.reportVersion,
      selectedPage: state.selectedPage,
      selectedLayout: state.selectedLayout,

      // derived state
      entity,
      portfolio,
      pages,
      isDrawerOpen,

      setReport,
      setSelectedPage,
      setSelectedLayout,
      addPage,
      removePage,
      nextPage,
      previousPage,
      onComponentLoad,
      resetPageSlot,
      applyLayout,
      toggleDrawer,
      openDrawer,
      closeDrawer,
    }),
    [
      state,
      entity,
      portfolio,
      pages,
      isDrawerOpen,
      setReport,
      setSelectedPage,
      setSelectedLayout,
      addPage,
      removePage,
      nextPage,
      previousPage,
      onComponentLoad,
      resetPageSlot,
      applyLayout,
      toggleDrawer,
      openDrawer,
      closeDrawer,
    ],
  );

  return (
    <EditSmartReportContext.Provider value={contextValue}>
      {children}
    </EditSmartReportContext.Provider>
  );
};

const useEditSmartReport = (): EditSmartReportContextType => {
  const context = useContext(EditSmartReportContext);
  if (!context) {
    throw new Error('useEditSmartReport must be used within EditSmartReportProvider');
  }
  return context;
};

export { EditSmartReportProvider, useEditSmartReport };
